import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Container } from "@/components/ui-blocks";
import { fetchOrderByLookup } from "@/services/api";
import { useCart } from "@/context/CartContext";
import { Check, ArrowRight } from "lucide-react";

export default function OrderConfirmation() {
  const { orderId } = useParams();
  const { clear } = useCart();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrderByLookup({ orderId }).then((o) => {
      setOrder(o);
      setLoading(false);
    });
    // Customer has returned from the payment page — clear the cart.
    clear();
  }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="order-confirmation-page" className="bg-[#FDFBF7] min-h-[70vh]">
      <Container>
        <div className="py-20 max-w-2xl mx-auto text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#6e5022] flex items-center justify-center">
            <Check className="w-7 h-7 text-[#FDFBF7]" strokeWidth={2} />
          </div>
          <div className="overline text-[#946f36] mt-6">Order Received</div>
          <h1 className="mt-3 font-serif-display text-5xl font-light">
            Thank you for your order
          </h1>
          <p className="mt-4 text-[#4A3728]">
            We&apos;ve received your order and sent a confirmation to your email.
            You will hear from us once payment is confirmed and your order is
            being prepared.
          </p>

          {loading ? (
            <div className="mt-10 animate-pulse text-[#946f36]">Loading order…</div>
          ) : order ? (
            <div className="mt-10 border border-[#6e5022]/30 p-7 text-left">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <div>
                  <div className="overline text-[#946f36]">Order Number</div>
                  <div data-testid="confirmation-order-id" className="font-serif-display text-2xl">
                    {order.orderId}
                  </div>
                </div>
                <Link
                  to="/track"
                  data-testid="confirmation-track-link"
                  className="border border-[#6e5022] text-[#6e5022] hover:bg-[#6e5022] hover:text-[#FDFBF7] px-5 py-2.5 text-xs uppercase tracking-[0.18em] transition-colors inline-flex items-center gap-2"
                >
                  Track Order <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                </Link>
              </div>
              <div className="space-y-2 text-sm">
                {order.products.map((p, i) => (
                  <div key={i} className="flex justify-between" data-testid={`confirmation-line-${i}`}>
                    <span className="text-[#4A3728]">{p.title} × {p.qty}</span>
                    <span>SGD {(p.price * p.qty).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              {/* Orders placed before bulk pricing existed have no breakdown;
                  showing a subtotal identical to the total would only confuse. */}
              {order.subtotal !== undefined && (
                <div className="border-t border-[#6e5022]/30 mt-4 pt-4 space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#4A3728]">Subtotal</span>
                    <span data-testid="confirmation-subtotal">SGD {Number(order.subtotal).toFixed(2)}</span>
                  </div>
                  {(order.discountLines || []).map((d, i) => (
                    <div key={d.ruleId || i} className="flex justify-between text-green-800">
                      <span>{d.label}</span>
                      <span data-testid={`confirmation-discount-${i}`}>− SGD {Number(d.amount).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between">
                    <span className="text-[#4A3728]">Delivery</span>
                    <span
                      data-testid="confirmation-shipping"
                      className={Number(order.shipping) === 0 ? "text-green-800" : ""}
                    >
                      {Number(order.shipping) === 0
                        ? order.freeDeliveryLabel || "Free"
                        : `SGD ${Number(order.shipping).toFixed(2)}`}
                    </span>
                  </div>
                  {Number(order.deliveryLeadDays) > 0 && (
                    <p className="text-xs text-[#946f36] pt-1.5 leading-relaxed" data-testid="confirmation-lead-time">
                      Deliveries to {order.shippingZoneLabel || "your address"} take a
                      minimum of {order.deliveryLeadDays} working
                      {Number(order.deliveryLeadDays) === 1 ? " day" : " days"} to fulfil
                      from the date of order.
                    </p>
                  )}
                </div>
              )}
              <div className="border-t border-[#6e5022]/30 mt-4 pt-4 flex justify-between font-serif-display text-2xl">
                <span>Total</span>
                <span data-testid="confirmation-total">SGD {order.total.toFixed(2)}</span>
              </div>
            </div>
          ) : (
            <div className="mt-10 text-[#946f36]">
              Order details unavailable. Please contact{" "}
              <a href="mailto:info@aceasia.com.sg" className="underline">info@aceasia.com.sg</a>.
            </div>
          )}

          <div className="mt-10 flex justify-center gap-3 flex-wrap">
            <Link to="/" data-testid="confirmation-home-link" className="bg-[#6e5022] hover:bg-[#946f36] text-[#FDFBF7] px-7 py-3.5 text-xs uppercase tracking-[0.2em] transition-colors">
              Back to Home
            </Link>
            <Link to="/shop" data-testid="confirmation-shop-link" className="border border-[#6e5022] text-[#6e5022] hover:bg-[#6e5022] hover:text-[#FDFBF7] px-7 py-3.5 text-xs uppercase tracking-[0.2em] transition-colors">
              Continue Shopping
            </Link>
          </div>
        </div>
      </Container>
    </div>
  );
}
